/**
 * Copyright (c) 2023 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Simon Paidla <s.paidla@techdivision.com>
 */

import React, { useState } from 'react';
import { Form, Formik } from 'formik';
import {
  Box, Button, Step, Stepper, StepButton,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PropTypes from 'prop-types';
import { useTrialSessionForm } from './useTrialSessionForm';
import TrialSessionSuccess from './TrialSessionSuccess/TrialSessionSuccess';
import StudioForm from './FormSteps/StudioForm/StudioForm';
import AppointmentForm from './FormSteps/AppointmentForm/AppointmentForm';
import UserForm from './FormSteps/UserForm/UserForm';
import SummaryForm from './FormSteps/SummaryForm/SummaryForm';
import trialSessionFormModel from './FormModel/FormModel';
import trialSessionFormInitialValues from './FormModel/FormInitialValues';
import { useSitePackageProvider } from '../Provider/SitePackageProvider';
import { Sanitize } from '../Components/Sanitizer';
import LoadingOverlay from '../Components/LoadingOverlay';
import ErrorBoundary from '../Components/Errors/ErrorBoundary';
import MagiclineErrorHandler from '../Components/Errors/MagiclineErrorHandler';
import { useTrialSessionValidatorGenerator } from './FormModel/useTrialSessionValidatorGenerator';
import './styles.scss';

/**
 * The magicline trial session app
 *
 * @author Team Codebaboons <codebaboons@techdivision.com>
 * @author Simon Paidla <s.paidla@techdivision.com>
 * @copyright 2023 TechDivision GmbH <info@techdivision.com>
 */
export function TrialSessionForm(props) {
  const { themeClass } = props;
  const [nextButtonIsActive, setNextButtonIsActive] = useState(true);
  const { generateSchema } = useTrialSessionValidatorGenerator();
  const trialSessionFormValidationSchema = generateSchema();
  const { formId, formField } = trialSessionFormModel;
  const { t } = useTranslation();

  const {
    privacyPolicyConsent,
    marketingConsent,
    marketingConsentIsRequired,
    underageResource,
    context,
    sitePackage,
    studioWhitelist,
  } = useSitePackageProvider();

  const {
    firstStepHeader,
    firstStepContent,
    secondStepHeader,
    secondStepContent,
    thirdStepHeader,
    thirdStepContent,
    fourthStepHeader,
    fourthStepContent,
    confirmationHeader,
    confirmationContent,
    stepperLabels,
    activeStep,
    lastValidStep,
    setLastValidStep,
    changeActiveStep,
    isLastStep,
    isLoading,
    errorCode,
    scrollToStepper,
    handleSubmit,
    handleBack,
  } = useTrialSessionForm(setNextButtonIsActive, sitePackage);

  /**
     * Render form step headers
     * @param step
     * @returns {JSX.Element}
     */
  const renderStepHeader = (step) => {
    switch (step) {
      case 0:
        return <Sanitize value={firstStepHeader} />;
      case 1:
        return <Sanitize value={secondStepHeader} />;
      case 2:
        return <Sanitize value={thirdStepHeader} />;
      case 3:
        return <Sanitize value={fourthStepHeader} />;
      case 4:
        return <Sanitize value={confirmationHeader} />;
      default:
        return <div>No Header Found</div>;
    }
  };

  /**
     * Render form step content
     * @param step
     * @returns {JSX.Element}
     */
  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <StudioForm
            formField={formField}
            firstStepHeader={firstStepHeader}
            firstStepContent={firstStepContent}
            context={context}
            studioWhitelist={studioWhitelist}
            sitePackage={sitePackage}
            resetValidSteps={() => setLastValidStep(activeStep)}
          />
        );
      case 1:
        return (
          <AppointmentForm
            formField={formField}
            setNextButtonIsActive={setNextButtonIsActive}
            secondStepContent={secondStepContent}
          />
        );
      case 2:
        return (
          <UserForm
            formField={formField}
            thirdStepContent={thirdStepContent}
            privacyPolicyConsent={privacyPolicyConsent}
            marketingConsent={marketingConsent}
            marketingConsentIsRequired={marketingConsentIsRequired}
            underageResource={underageResource}
            resetValidSteps={() => setLastValidStep(activeStep)}
          />
        );
      case 3:
        return <SummaryForm fourthStepContent={fourthStepContent} />;
      default:
        return <div>Not Content Found</div>;
    }
  };

  return (
    <Box>
      {renderStepHeader(activeStep)}
      <Stepper
        nonLinear
        className="form-stepper"
        activeStep={activeStep}
        alternativeLabel
        sx={{
          '& .MuiStepConnector-line': {
            borderTopWidth: '4px',
          },
          '& .MuiStepConnector-root.Mui-active .MuiStepConnector-line': {
            borderColor: 'primary.main',
          },
          '& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': {
            borderColor: 'primary.main',
          },
        }}
      >
        {stepperLabels.map((label, index) => {
          const isStepDisabled = index > lastValidStep;
          return (
            <Step key={label} completed={(index < lastValidStep)}>
              <StepButton
                onClick={() => changeActiveStep(index)}
                disabled={isStepDisabled}
                aria-disabled={isStepDisabled}
                tabIndex={isStepDisabled ? -1 : 0}
                icon={index < lastValidStep ? <CheckCircleIcon color="primary" /> : index + 1}
              >
                {label}
              </StepButton>
            </Step>
          );
        })}
      </Stepper>
      <ErrorBoundary>
        {isLoading ? <LoadingOverlay /> : null}
        {errorCode ? <MagiclineErrorHandler errorCode={errorCode} /> : null}
        {activeStep === stepperLabels.length ? (
          <TrialSessionSuccess confirmationContent={confirmationContent} />) : (
            <Formik
              initialValues={trialSessionFormInitialValues}
              validationSchema={trialSessionFormValidationSchema[activeStep]}
              onSubmit={handleSubmit}
            >
              <Form id={formId} className="trial-session-form" data-theme={themeClass}>
                {renderStepContent(activeStep)}
                <Box className="form-action-wrapper">
                  {activeStep !== 0 && (
                    <Button variant="outlined" onClick={handleBack} className="form-back-button">
                      {t('prevPage')}
                    </Button>
                  )}
                  <Box>
                    <Button
                      disabled={!nextButtonIsActive}
                      type="submit"
                      variant="contained"
                      color="primary"
                      className="form-next-button"
                      onClick={scrollToStepper}
                    >
                      {isLastStep ? t('submit') : t('nextPage')}
                    </Button>
                  </Box>
                </Box>
              </Form>
            </Formik>
        )}
      </ErrorBoundary>
    </Box>
  );
}

TrialSessionForm.propTypes = {
  themeClass: PropTypes.string.isRequired,
};
